import {
    Col,
    Row,
    TableColumnsType,
    Tooltip,
    Button as ButtonAnt,
    Typography,
    App
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Table } from '@/components/tables/table';
import { ComboFoodManagementSearcher } from '@/components/forms/combo-food-management/search.form';
import { css } from '@emotion/react';
import {
    watchComboFoodLoading,
    watchComboFoodMessage,
    resetMessages
} from '@/redux/slices/combo-food.slice';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useState } from 'react';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import { setMessages } from '@/redux/slices/app.slice';
import {
    deleteHealthNutritionProfilesComboFood,
    getListHealthNutritionProfilesComboFood,
    getListAgeComboFood,
    getListGenderComboFood,
    getListPhysicalActivityComboFood,
    getListNutritionHealthStatusComboFood,
    getListNutritionStatusComboFood,
    getListNutritionStatusZScoreComboFood,
    getListNutritionStatus60to19ComboFood,
    watchListGenderComboFood,
    watchListAgeComboFood,
    watchListPhysicalActivityComboFood,
    watchListNutritionHealthStatusComboFood,
    watchListNutritionStatusComboFood,
    watchListNutritionStatusZScoreComboFood,
    watchListNutritionStatus60to19ComboFood,
    watchListHealthNutritionProfilesComboFood,
    watchComboFoodPagination
} from '@/redux/slices/combo-food.slice';
import { PageURLs } from '@/utils/navigate';
import { PermissionMode } from '@/enums/mode.enum';
import { useQuery } from '@/hooks/query.hook';
import usePermission from '@/hooks/permission.hook';
import { formatDateToHHmmDDMMYYYY } from '@/utils/common';

type PageChangeParams = {
    page?: number;
    pageSize?: number;
    customFilter?: FilterComboFood;
};

export default function ComboFoodManagementTable() {
    const { updatable, deletable } = usePermission();
    const dispatch = useAppDispatch();
    const listAge = useAppSelector(watchListAgeComboFood);
    const listGender = useAppSelector(watchListGenderComboFood);
    const listPhysicalActivityComboFood = useAppSelector(watchListPhysicalActivityComboFood);
    const listNutritionHealthStatusComboFood = useAppSelector(watchListNutritionHealthStatusComboFood);
    const listNutritionStatusComboFood = useAppSelector(watchListNutritionStatusComboFood);
    const listNutritionStatusZScoreComboFood = useAppSelector(watchListNutritionStatusZScoreComboFood);
    const listNutritionStatus60to19ComboFood = useAppSelector(watchListNutritionStatus60to19ComboFood);
    const listHealthNutritionProfilesComboFood = useAppSelector(watchListHealthNutritionProfilesComboFood);
    const [filter, setFilter] = useState<FilterComboFood>({});
    const pagination = useAppSelector(watchComboFoodPagination);
    const loading = useAppSelector(watchComboFoodLoading);
    const messageInfo = useAppSelector(watchComboFoodMessage);
    const { modal } = App.useApp();
    const { push } = useQuery();

    const handlePageChange = useCallback(
        ({
            page = PAGINATION_PARAMS.offset,
            pageSize = PAGINATION_PARAMS.limit,
            customFilter = filter
        }: PageChangeParams) => {
            const newFilter = { ...customFilter };

            if (Array.isArray(newFilter.HEALTH_STATUS_ID) && newFilter.HEALTH_STATUS_ID.length === 0) {
                newFilter.HEALTH_STATUS_ID = undefined;
            }

            if (
                Array.isArray(newFilter.PHYSICAL_ACTIVITY_LEVEL) &&
                newFilter.PHYSICAL_ACTIVITY_LEVEL.length === 0
            ) {
                newFilter.PHYSICAL_ACTIVITY_LEVEL = undefined;
            }

            dispatch(getListHealthNutritionProfilesComboFood({
                customFilter: newFilter,
                page,
                limit: pageSize
            }));
        },
        [dispatch, filter]
    );

    const handleSearch = (values: FilterComboFood) => {
        const isReset = Object.keys(values).length === 0;
        const newFilter = isReset ? {} : { ...filter, ...values };
        setFilter(newFilter);
        handlePageChange({ customFilter: newFilter });
    };
    const handleDeleteComboFood = async (id: number) => {
        await dispatch(deleteHealthNutritionProfilesComboFood(id));
        const remainingRecords = listHealthNutritionProfilesComboFood.length - 1;
        if (remainingRecords === 0 && pagination.curPage > 1) {
            handlePageChange({
                page: pagination.curPage - 1,
                pageSize: pagination.limitPage,
                customFilter: filter
            });
        } else {
            handlePageChange({
                page: pagination.curPage,
                pageSize: pagination.limitPage,
                customFilter: filter
            });
        }
    };
    const confirmDeleteComboFood = (id: number) => {
        modal.confirm({
            title: 'Bạn có chắc chắn xoá combo thực phẩm này?',
            content: (
                <em>
                    Dữ liệu không thể khôi phục
                </em>
            ),
            okText: 'Xóa',
            width: 450,
            cancelText: 'Hủy',
            onOk: () => handleDeleteComboFood(id)
        });
    };

    useEffect(() => {
        if (messageInfo) {
            dispatch(
                setMessages([
                    {
                        type: messageInfo.type,
                        message: messageInfo.message,
                        description: messageInfo.description
                    }
                ])
            );

            dispatch(resetMessages());
        }
    }, [dispatch, messageInfo]);

    useEffect(() => {
        dispatch(getListAgeComboFood());
        dispatch(getListGenderComboFood());
        dispatch(getListPhysicalActivityComboFood());
        dispatch(getListNutritionHealthStatusComboFood());
        dispatch(getListNutritionStatusComboFood());
        dispatch(getListNutritionStatusZScoreComboFood());
        dispatch(getListNutritionStatus60to19ComboFood());
    }, [dispatch])

    useEffect(() => {
        handlePageChange({});
    }, [handlePageChange]);

    const handleEdit = (comboFoodID: number) => {
        push(`${PageURLs.ofComboFoodManagement()}?mode=${PermissionMode.EDIT}&comboFoodId=${comboFoodID}`);
    }

    const columns: TableColumnsType<any> = [
        {
            title: 'STT',
            render: (_, __, index) => (pagination.curPage - 1) * pagination.limitPage + index + 1,
            width: 50,
        },
        {
            title: 'Độ tuổi',
            dataIndex: 'AGE_RANGE_DESCRIPTION',
            key: 'AGE_RANGE_DESCRIPTION',
            width: 90,
            fixed: 'left',
            align: 'center',
        },
        {
            title: 'Tình trạng sức khỏe',
            dataIndex: 'HEALTH_STATUS_NAME',
            key: 'HEALTH_STATUS_NAME',
            width: 150,
            align: 'center',
        },
        {
            title: 'Giới tính',
            dataIndex: 'GENDER_NAME',
            key: 'GENDER_NAME',
            width: 90,
            align: 'center',
        },
        {
            title: 'Mức độ hoạt động thể lực',
            dataIndex: 'ACTIVITY_VALUE',
            key: 'ACTIVITY_VALUE',
            width: 150,
            align: 'center',
        },
        {
            title: 'Cân nặng',
            dataIndex: 'WEIGHT',
            key: 'WEIGHT',
            width: 110,
            align: 'center',
            render: (_, record) => {
                const min = record.PROFILE_WEIGHT_MIN ?? '';
                const max = record.PROFILE_WEIGHT_MAX ?? '';
                return `${min} - ${max}`;
            }
        },
        {
            title: 'Tình trạng dinh dưỡng theo Zscore, cân nặng theo tuổi đối với trẻ dưới 60 tháng',
            dataIndex: 'ZSCORE_DESCRIPTION',
            key: 'ZSCORE_DESCRIPTION',
            width: 200,
            align: 'center',
        },
        {
            title: 'Tình trạng dinh dưỡng theo BMI đối với trẻ từ 60 tháng đến tròn 19 tuổi',
            dataIndex: 'BMI_60_19_DESCRIPTION',
            key: 'BMI_60_19_DESCRIPTION',
            width: 200,
            align: 'center',
        },
        {
            title: 'Tình trạng dinh dưỡng theo BMI đối với người > 19 tuổi',
            dataIndex: 'BMI_GE_19_DESCRIPTION',
            key: 'BMI_GE_19_DESCRIPTION',
            width: 200,
            align: 'center',
        },
        {
            title: 'Ngày tạo',
            dataIndex: 'PROFILE_CREATED_DATE',
            key: 'PROFILE_CREATED_DATE',
            width: 100,
            align: 'center',
            render: (_, record) => {
                const createdAt = record.PROFILE_CREATED_DATE;
                const createdBy = record.PROFILE_CREATED_NAME;
                return `${formatDateToHHmmDDMMYYYY(createdAt)}${createdBy ? ' ' + createdBy + ' ' : ''}`;
            }
        },
        {
            title: 'Cập nhật mới nhất',
            dataIndex: 'PROFILE_MODIFIED_DATE',
            key: 'PROFILE_MODIFIED_DATE',
            width: 100,
            align: 'center',
            render: (_, record) => {
                const updatedAt = record.PROFILE_MODIFIED_DATE;
                const updatedBy = record.PROFILE_UPDATED_NAME;
                return `${formatDateToHHmmDDMMYYYY(updatedAt)}${updatedBy ? ' ' + updatedBy + ' ' : ''}`;
            }
        },
        {
            title: 'Thao tác',
            key: 'action',
            fixed: 'right',
            width: 104,
            render: (_, record) => (
                <Row gutter={4} wrap={false}>
                    {updatable && (
                        <Col>
                            <Tooltip title='Cập nhật'>
                                <ButtonAnt
                                    type='primary'
                                    icon={<EditOutlined />}
                                    css={editButtonStyles}
                                    onClick={() => handleEdit(record.PROFILE_ID)}
                                />
                            </Tooltip>
                        </Col>
                    )}
                    {deletable && (
                        <Col>
                            <Tooltip title='Xoá'>
                                <ButtonAnt
                                    onClick={() => confirmDeleteComboFood(record.PROFILE_ID)}
                                    type='primary'
                                    danger
                                    icon={<DeleteOutlined />}
                                />
                            </Tooltip>
                        </Col>
                    )}
                </Row>
            )
        }
    ];
    return (
        <div css={rootStyles}>
            <ComboFoodManagementSearcher
                onSearch={handleSearch}
                listAge={listAge}
                listGender={listGender}
                listPhysicalActivityComboFood={listPhysicalActivityComboFood}
                listNutritionHealthStatusComboFood={listNutritionHealthStatusComboFood}
                listNutritionStatusComboFood={listNutritionStatusComboFood}
                listNutritionStatusZScoreComboFood={listNutritionStatusZScoreComboFood}
                listNutritionStatus60to19ComboFood={listNutritionStatus60to19ComboFood}
            />

            <div css={boxStyles}>
                <Typography.Title level={5}>Danh sách</Typography.Title>

                <Table<any>
                    columns={columns}
                    dataSource={listHealthNutritionProfilesComboFood}
                    loading={loading}
                    pagination={{
                        showQuickJumper: false,
                        showSizeChanger: true,
                        current: pagination.curPage,
                        pageSize: pagination.limitPage,
                        total: pagination.totalRows,
                        onChange: (page, pageSize) => {
                            handlePageChange({ page, pageSize });
                        }
                    }}
                />
            </div>
        </div>
    );
}

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }

  .ant-table-thead > tr > th {
    background: var(--blue-dark-color) !important;
    color: var(--white-color);
  }

  .ant-table-cell::before {
    display: none;
  }
`;

const editButtonStyles = css`
  background-color: var(--yellow-light-color) !important;
  color: var(--white-color);
`;